import type { ChatMode } from "@/store/chat-state";
import type { ChatParts, UIMessageWithDataTypes } from "@/types/chat-parts";
import { useChat } from "@ai-sdk/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isActiveChatStatus,
  isReadyChatStatus,
} from "@/constants/chat-statuses";
import { EMPTY_MESSAGES } from "@/constants/empty-messages";
import { CHAT_MODE, useChatState } from "@/store/chat-state";
import { handleChatError } from "@/utils/chat-error-handler";
import { mapResponseToChatParts } from "@/utils/map-response-to-chat-parts";
import { useChatConfig } from "./use-chat-config";
import { useWidgetRuntimeConfig } from "@/context/widget-runtime-config";
import { isTextUIPart } from "ai";

export type ChatManagerResult = {
  input: string;
  setInput: (input: string) => void;
  messages: UIMessageWithDataTypes[];
  status: "error" | "submitted" | "streaming" | "ready";
  error: Error | undefined;
  userQuery: string;
  contextUrl?: string;
  response: ChatParts | undefined;
  mode: ChatMode;
  setMode: (mode: ChatMode) => void;
  setUserQuery: (query: string) => void;
  resetChat: () => void;
};

export const useChatManager = (): ChatManagerResult => {
  const { userQuery, contextUrl, widgetOrigin, mode, setMode, setUserQuery, setContextUrl } = useChatState();
  const { apiUrl, apiKey } = useWidgetRuntimeConfig();

  // Use refs to always get the latest values from async callbacks.
  const contextUrlRef = useRef(contextUrl);
  const widgetOriginRef = useRef(widgetOrigin);

  useEffect(() => {
    contextUrlRef.current = contextUrl;
  }, [contextUrl]);

  useEffect(() => {
    widgetOriginRef.current = widgetOrigin;
  }, [widgetOrigin]);

  const chatConfig = useChatConfig({
    apiUrl,
    apiKey,
    getContextUrl: () => contextUrlRef.current,
    getWidgetOrigin: () => widgetOriginRef.current,
  });

  const {
    messages,
    setMessages,
    sendMessage,
    stop,
    status,
    error,
  } = useChat<UIMessageWithDataTypes>(chatConfig);

  // Manage input state locally (no longer provided by useChat in v5)
  const [input, setInput] = useState("");

  const response = useMemo(() => {
    const lastMessage = messages.at(-1);

    // Check if we have an assistant message with text content
    const hasContent = lastMessage?.role === "assistant"
      && lastMessage?.parts?.some(part => isTextUIPart(part));

    if (!hasContent || !lastMessage) {
      return undefined;
    }

    // Map the response regardless of status (allows streaming)
    return mapResponseToChatParts(lastMessage);
  }, [messages]);

  const prepareForNewMessage = useCallback(() => {
    // Resets chat state: stops any active stream, clears messages
    if (isActiveChatStatus(status)) {
      stop();
    }
    setMessages(EMPTY_MESSAGES);
  }, [status, stop, setMessages]);

  const resetChat = useCallback(() => {
    // Clear user query first to prevent auto-send
    setUserQuery("");
    // Clear contextUrl to prevent stuck summarization state
    setContextUrl(undefined);
    // Prepare for new message (stops active chat, clears messages, resets state)
    prepareForNewMessage();
    // Finally set mode to INPUT (this won't trigger reset because userQuery is already empty)
    setMode(CHAT_MODE.INPUT);
  }, [setMode, setUserQuery, setContextUrl, prepareForNewMessage]);

  useEffect(() => {
    /**
     * TWO-STEP CHAT SUBMISSION FLOW
     *
     * Step 1: PREPARE - Reset chat when user has a query in INPUT mode
     * Step 2: SEND - Actually send the message when chat becomes ready
     *
     * This separation allows for:
     * - Immediate re-submission of the same query
     * - Clean interruption of active streams
     * - No waiting for mode changes between submissions
     */

    // Step 1: PREPARE - Always prepare for new message when there's a query
    // This handles both new queries and interruptions of active streams
    if (mode === CHAT_MODE.INPUT && userQuery.trim().length > 0) {
      prepareForNewMessage();
    }

    // Step 2: SEND - Send the prepared message once chat is ready
    // Note: Both conditions must be checked to avoid sending during preparation
    if (
      mode === CHAT_MODE.INPUT
      && isReadyChatStatus(status)
      && userQuery.trim().length > 0
    ) {
      (async () => {
        try {
          const contextUrlForMessage = contextUrl;
          setMode(CHAT_MODE.ANSWERING);
          await sendMessage({
            // If there is a context URL, use that instead of user query, this will be summarized
            text: contextUrlForMessage ?? userQuery,
          });
          if (
            contextUrlForMessage
            && contextUrlRef.current === contextUrlForMessage
          ) {
            setContextUrl(undefined);
          }
        }
        catch (error) {
          handleChatError(error as Error, "auto-send", {
            content: userQuery,
            apiUrl,
          });
        }
      })();
    }

    // No action for other cases:
    // - Empty queries are ignored (no API calls)
    // - Status transitions handle themselves through the two-step flow
  }, [mode, status, userQuery, prepareForNewMessage, setMode, sendMessage, contextUrl]);

  return {
    // State
    input,
    messages,
    status,
    error,
    userQuery,
    contextUrl,
    response,
    mode,
    // Actions
    setInput,
    setMode,
    setUserQuery,
    resetChat,
  };
};
